import { useEffect, useState } from "react";
import { api } from "../lib/api";
import {
  ArrowLeft, Users, CreditCard, ShoppingCart, Mail, TrendingUp, LayoutDashboard,
  Download, Copy, Send, Loader2, ShieldAlert, VolumeX, School, UserPlus, Key, Trash2,
} from "lucide-react";
import { toast } from "sonner";
const TABS = [
  { id: "overview",    label: "Podsumowanie", icon: LayoutDashboard },
  { id: "users",       label: "Użytkownicy",  icon: Users },
  { id: "subscribers", label: "Subskrybenci", icon: CreditCard },
  { id: "purchases",   label: "Zakupy",       icon: ShoppingCart },
  { id: "email",       label: "Wyślij mail",  icon: Mail },
  { id: "moderation",  label: "Moderacja",    icon: ShieldAlert },
  { id: "schoolaccts", label: "Konta szkół",  icon: School },
];

function fmtDate(iso) {
  try { return new Date(iso).toLocaleString("pl-PL", { dateStyle: "short", timeStyle: "short" }); } catch { return ""; }
}
function fmtPLN(v) {
  return `${(v ?? 0).toFixed(2).replace(".", ",")} zł`;
}

export default function AdminPanel({ onBack }) {
  const [tab, setTab] = useState("overview");
  const [overview, setOverview] = useState(null);
  const [users, setUsers] = useState(null);
  const [subs, setSubs] = useState(null);
  const [purchases, setPurchases] = useState(null);
  const [refreshingAt, setRefreshingAt] = useState(null);

  const load = async () => {
    try {
      const [o, u, s, p] = await Promise.all([
        api.get("/admin/overview"),
        api.get("/admin/users"),
        api.get("/admin/subscribers"),
        api.get("/admin/purchases"),
      ]);
      setOverview(o.data);
      setUsers(u.data);
      setSubs(s.data);
      setPurchases(p.data);
      setRefreshingAt(new Date());
    } catch (e) {
      toast.error(e.response?.data?.detail || "Nie udało się wczytać danych");
    }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="admin-panel">
      <header className="w-full border-b border-slate-200 sticky top-0 bg-white/95 backdrop-blur z-10">
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <button
            onClick={onBack}
            data-testid="admin-back-button"
            className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900"
          >
            <ArrowLeft className="w-4 h-4" /> Wróć do aplikacji
          </button>
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <LayoutDashboard className="w-4 h-4 text-slate-900" /> Panel administratora
          </div>
          <button
            onClick={load}
            data-testid="admin-refresh-button"
            className="text-xs font-bold text-slate-600 hover:text-slate-900 border border-slate-200 rounded-full px-3 py-1.5"
          >
            Odśwież
          </button>
        </div>
      </header>

      <div className="max-w-[1100px] w-full mx-auto px-4 sm:px-6 py-6 flex-1">
        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6" role="tablist">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                role="tab"
                aria-selected={active}
                data-testid={`admin-tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`inline-flex items-center gap-2 text-sm font-bold px-4 py-2 rounded-full border transition-colors ${
                  active
                    ? "bg-slate-900 text-white border-slate-900"
                    : "bg-white text-slate-600 border-slate-200 hover:border-slate-900"
                }`}
              >
                <Icon className="w-3.5 h-3.5" /> {t.label}
              </button>
            );
          })}
        </div>

        {tab === "overview" && <Overview data={overview} />}
        {tab === "users" && <UsersTab data={users} />}
        {tab === "subscribers" && <SubscribersTab data={subs} />}
        {tab === "purchases" && <PurchasesTab data={purchases} />}
        {tab === "email" && <EmailBlastTab users={users} />}
        {tab === "moderation" && <ModerationTab />}
        {tab === "schoolaccts" && <SchoolAccountsTab />}

        {refreshingAt && (
          <div className="mt-8 text-xs text-slate-400 text-center">
            Ostatnia aktualizacja: {refreshingAt.toLocaleTimeString("pl-PL")}
          </div>
        )}
      </div>
    </div>
  );
}

/* -------------------- Overview -------------------- */
function Overview({ data }) {
  if (!data) return <Skeleton />;
  const kpis = [
    { label: "Użytkownicy (łącznie)", value: data.users.total, sub: `+${data.users.new_7d} w 7 dni`, testid: "kpi-users" },
    { label: "Aktywni subskrybenci", value: data.subscribers.active, sub: `${data.subscribers.canceled} anulowanych`, testid: "kpi-active-subs" },
    { label: "Przychód (łącznie)", value: fmtPLN(data.revenue.total_pln), sub: `${data.revenue.paid_transactions} transakcji`, testid: "kpi-revenue" },
    { label: "Zrobionych quizów", value: data.activity.quizzes_completed_total, sub: `+${data.activity.quizzes_completed_7d} w 7 dni`, testid: "kpi-quizzes" },
  ];

  return (
    <div>
      <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
        Podsumowanie
      </h1>
      <p className="text-slate-500 mt-1">Wszystko, co dzieje się w SocialStart, w jednym miejscu.</p>

      <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-3">
        {kpis.map((k) => (
          <div key={k.label} className="bg-white border border-slate-200 rounded-3xl p-5" data-testid={k.testid}>
            <div className="text-xs font-bold uppercase tracking-wider text-slate-400">{k.label}</div>
            <div className="mt-2 text-3xl font-extrabold tracking-tight text-slate-900">{k.value}</div>
            <div className="mt-1 text-xs text-slate-500 flex items-center gap-1">
              <TrendingUp className="w-3 h-3 text-yellow-500" /> {k.sub}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-8 grid sm:grid-cols-2 gap-3">
        <div className="bg-white border border-slate-200 rounded-3xl p-5">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">Subskrybenci wg planu</div>
          <ul className="space-y-2 text-sm">
            <PlanRow label="Basic (19,99 zł/m-c)" n={data.subscribers.by_plan.basic} />
            <PlanRow label="Premium (49,99 zł/m-c)" n={data.subscribers.by_plan.premium} />
            <PlanRow label="Szkoły (119,99 zł/m-c)" n={data.subscribers.by_plan.schools} />
          </ul>
        </div>
        <div className="bg-white border border-slate-200 rounded-3xl p-5">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">Wzrost</div>
          <ul className="space-y-2 text-sm">
            <RowKV k="Nowi użytkownicy (7 dni)" v={data.users.new_7d} />
            <RowKV k="Nowi użytkownicy (30 dni)" v={data.users.new_30d} />
            <RowKV k="Zgody na marketing" v={data.users.with_marketing_consent} />
            <RowKV k="Utworzone klasy" v={data.activity.classes_created} />
          </ul>
        </div>
      </div>
    </div>
  );
}
function PlanRow({ label, n }) {
  return (
    <li className="flex items-center justify-between border-b border-slate-100 pb-2 last:border-0 last:pb-0">
      <span className="text-slate-700">{label}</span>
      <b className="text-slate-900">{n}</b>
    </li>
  );
}
function RowKV({ k, v }) {
  return (
    <li className="flex justify-between text-slate-700">
      <span>{k}</span>
      <b className="text-slate-900">{v}</b>
    </li>
  );
}

/* -------------------- Users -------------------- */
function UsersTab({ data }) {
  if (!data) return <Skeleton />;
  const [query, setQuery] = [null, null]; // placeholder, kept simple
  const users = data.users;

  const copyEmails = () => {
    const list = users.map((u) => u.email).join(", ");
    navigator.clipboard?.writeText(list);
    toast.success(`Skopiowano ${users.length} adresów`);
  };
  const downloadCsv = async () => {
    try {
      const r = await api.get("/admin/users.csv", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `socialstart_users_${new Date().toISOString().slice(0,10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Nie udało się pobrać CSV"); }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">Baza użytkowników</h1>
          <p className="text-slate-500 mt-1">Wszyscy zalogowani przez Google, z aktualną zgodą marketingową.</p>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="copy-emails-button"
            onClick={copyEmails}
            className="inline-flex items-center gap-2 text-sm font-bold text-slate-900 bg-white border border-slate-200 hover:border-slate-900 rounded-2xl px-4 py-2"
          >
            <Copy className="w-4 h-4" /> Kopiuj wszystkie e-maile
          </button>
          <button
            data-testid="download-csv-button"
            onClick={downloadCsv}
            className="inline-flex items-center gap-2 text-sm font-bold text-slate-900 bg-[#FFD600] hover:bg-[#E6C200] rounded-2xl px-4 py-2"
          >
            <Download className="w-4 h-4" /> CSV
          </button>
        </div>
      </div>

      <div className="mt-6 bg-white border border-slate-200 rounded-3xl overflow-hidden">
        <div className="grid grid-cols-12 gap-3 px-4 py-3 bg-slate-50 border-b border-slate-200 text-xs font-bold uppercase tracking-wider text-slate-500">
          <div className="col-span-5">Email</div>
          <div className="col-span-4">Nazwa</div>
          <div className="col-span-3">Zarejestrowany</div>
        </div>
        <ul className="divide-y divide-slate-100" data-testid="admin-users-list">
          {users.map((u, i) => (
            <li key={i} className="grid grid-cols-12 gap-3 px-4 py-3 text-sm items-center">
              <div className="col-span-5 truncate font-medium text-slate-900">{u.email}</div>
              <div className="col-span-4 truncate text-slate-600">{u.name || "—"}</div>
              <div className="col-span-3 text-slate-500 text-xs">{fmtDate(u.created_at)}</div>
            </li>
          ))}
          {users.length === 0 && (
            <li className="px-4 py-8 text-center text-slate-400 text-sm">Brak użytkowników</li>
          )}
        </ul>
      </div>
      <div className="mt-3 text-xs text-slate-500">Łącznie: <b>{data.count}</b> (z aktywną zgodą marketingową)</div>
    </div>
  );
}

/* -------------------- Subscribers -------------------- */
function SubscribersTab({ data }) {
  if (!data) return <Skeleton />;
  return (
    <div>
      <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">Subskrybenci</h1>
      <p className="text-slate-500 mt-1">Aktywne i anulowane subskrypcje.</p>

      <div className="mt-6 bg-white border border-slate-200 rounded-3xl overflow-hidden">
        <div className="grid grid-cols-12 gap-3 px-4 py-3 bg-slate-50 border-b border-slate-200 text-xs font-bold uppercase tracking-wider text-slate-500">
          <div className="col-span-4">Email</div>
          <div className="col-span-2">Plan</div>
          <div className="col-span-2">Interval</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-2">Koniec cyklu</div>
        </div>
        <ul className="divide-y divide-slate-100" data-testid="admin-subscribers-list">
          {data.items.map((s, i) => (
            <li key={i} className="grid grid-cols-12 gap-3 px-4 py-3 text-sm items-center">
              <div className="col-span-4 truncate font-medium text-slate-900">{s.email}</div>
              <div className="col-span-2">
                <span className="text-xs font-bold uppercase tracking-wider bg-[#FEF9C3] border border-yellow-200 rounded-full px-2 py-0.5 text-slate-900">
                  {s.plan}
                </span>
              </div>
              <div className="col-span-2 text-slate-600 text-xs">{s.billing_interval === "year" ? "Roczny" : "Miesięczny"}</div>
              <div className="col-span-2">
                <span className={`text-xs font-bold rounded-full px-2 py-0.5 ${
                  s.status === "active"
                    ? "bg-green-50 text-green-700 border border-green-200"
                    : "bg-slate-100 text-slate-500 border border-slate-200"
                }`}>
                  {s.status}
                </span>
              </div>
              <div className="col-span-2 text-slate-500 text-xs">{fmtDate(s.cycle_end)}</div>
            </li>
          ))}
          {data.items.length === 0 && (
            <li className="px-4 py-8 text-center text-slate-400 text-sm">Brak subskrybentów</li>
          )}
        </ul>
      </div>
    </div>
  );
}

/* -------------------- Purchases -------------------- */
function PurchasesTab({ data }) {
  if (!data) return <Skeleton />;
  return (
    <div>
      <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">Zakupy</h1>
      <p className="text-slate-500 mt-1">Każda transakcja płatnicza w SocialStart.</p>

      <div className="mt-6 bg-white border border-slate-200 rounded-3xl overflow-hidden">
        <div className="grid grid-cols-12 gap-3 px-4 py-3 bg-slate-50 border-b border-slate-200 text-xs font-bold uppercase tracking-wider text-slate-500">
          <div className="col-span-4">Email</div>
          <div className="col-span-3">Plan</div>
          <div className="col-span-2">Kwota</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-1">Data</div>
        </div>
        <ul className="divide-y divide-slate-100" data-testid="admin-purchases-list">
          {data.items.map((t, i) => (
            <li key={i} className="grid grid-cols-12 gap-3 px-4 py-3 text-sm items-center">
              <div className="col-span-4 truncate font-medium text-slate-900">{t.email || "—"}</div>
              <div className="col-span-3 text-slate-600 text-xs truncate">{t.lookup_key}</div>
              <div className="col-span-2 font-bold text-slate-900">{fmtPLN(t.amount_pln)}</div>
              <div className="col-span-2">
                <span className={`text-xs font-bold rounded-full px-2 py-0.5 ${
                  t.payment_status === "paid"
                    ? "bg-green-50 text-green-700 border border-green-200"
                    : t.payment_status === "expired" || t.status === "expired"
                    ? "bg-red-50 text-red-600 border border-red-200"
                    : "bg-slate-100 text-slate-500 border border-slate-200"
                }`}>
                  {t.payment_status}
                </span>
              </div>
              <div className="col-span-1 text-slate-500 text-xs">{fmtDate(t.created_at)}</div>
            </li>
          ))}
          {data.items.length === 0 && (
            <li className="px-4 py-8 text-center text-slate-400 text-sm">Jeszcze żadnych zakupów</li>
          )}
        </ul>
      </div>
    </div>
  );
}

/* -------------------- Email blast (Brevo real send) -------------------- */
function EmailBlastTab({ users }) {
  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [subject, setSubject] = useState("Nowość w SocialStart 💡");
  const [body, setBody] = useState(
    "Cześć!\n\n" +
    "Chciałem/-am dać znać, że w SocialStart pojawiła się nowa funkcja...\n\n" +
    "Miłej nauki!\n" +
    "Zespół SocialStart"
  );
  const [sending, setSending] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [lastSyncResult, setLastSyncResult] = useState(null);
  const [lastSendResult, setLastSendResult] = useState(null);

  const recipientCount = users?.users?.length || 0;

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/brevo/status");
        setStatus(data);
      } catch (e) {
        setStatus({ configured: false, error: e.response?.data?.detail || "błąd" });
      } finally {
        setStatusLoading(false);
      }
    })();
  }, []);

  const syncNow = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/brevo/sync");
      setLastSyncResult(data);
      toast.success(`Zsynchronizowano ${data.synced} adresów`);
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === "object" ? JSON.stringify(detail).slice(0, 200) : (detail || "Błąd synchronizacji");
      toast.error(msg);
    } finally {
      setSyncing(false);
    }
  };

  const sendCampaign = async (sendNow = false) => {
    if (!subject.trim() || body.trim().length < 10) {
      toast.error("Wpisz temat i dłuższą treść");
      return;
    }
    if (sendNow && !window.confirm(`Wysłać kampanię do ~${recipientCount} odbiorców teraz? Tego nie da się cofnąć.`)) return;
    setSending(true);
    try {
      const { data } = await api.post("/admin/brevo/broadcast", { subject, body, send_now: sendNow });
      setLastSendResult(data);
      toast.success(sendNow ? `Kampania wysłana! ID: ${data.campaign_id}` : `Draft utworzony (ID: ${data.campaign_id})`);
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === "object" ? JSON.stringify(detail).slice(0, 300) : (detail || "Błąd wysyłki");
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">Wyślij mail marketingowy</h1>
      <p className="text-slate-500 mt-1 max-w-2xl">
        Wysyłamy przez Brevo z adresu <b>{status?.sender_email || "noreply@socialstart.store"}</b>. Odpowiedzi trafiają na <b>{status?.reply_to || "kontakt@socialstart.store"}</b>.
      </p>

      {/* Brevo status card */}
      <div className="mt-4 bg-white border border-slate-200 rounded-2xl p-4" data-testid="brevo-status">
        {statusLoading ? (
          <div className="text-sm text-slate-500">Sprawdzam status Brevo...</div>
        ) : status?.healthy ? (
          <div className="flex items-center gap-2 text-sm text-green-700">
            <span className="w-2 h-2 rounded-full bg-green-500" /> Brevo połączone (lista #{status.list_id})
          </div>
        ) : (
          <div className="text-sm">
            <div className="flex items-center gap-2 text-red-600 font-bold">
              <span className="w-2 h-2 rounded-full bg-red-500" /> Brevo niedostępne
            </div>
            <div className="mt-2 text-slate-600">
              Prawdopodobnie serwer nie jest na liście autoryzowanych IP w Brevo.
              Wejdź na <a href="https://app.brevo.com/security/authorised_ips" target="_blank" rel="noreferrer" className="text-slate-900 underline">app.brevo.com/security/authorised_ips</a> i albo <b>dodaj bieżący IP</b>, albo <b>wyłącz restrykcję IP</b> (rekomendowane dla API kluczy backendowych).
            </div>
          </div>
        )}
      </div>

      {/* Sync users */}
      <div className="mt-4 bg-white border border-slate-200 rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="text-sm text-slate-600">
          <b>Krok 1:</b> Zsynchronizuj bazę użytkowników z listą w Brevo (kliknij raz na jakiś czas albo po dodaniu nowych osób).
          {lastSyncResult && (
            <div className="mt-1 text-xs text-slate-500">
              Ostatnia synchronizacja: {lastSyncResult.synced} zgodnych, {lastSyncResult.blacklisted} bez zgody.
            </div>
          )}
        </div>
        <button
          data-testid="brevo-sync-button"
          onClick={syncNow}
          disabled={syncing || !status?.healthy}
          className="inline-flex items-center gap-2 text-sm font-bold text-slate-900 bg-white border border-slate-200 hover:border-slate-900 disabled:opacity-50 rounded-2xl px-4 py-2"
        >
          {syncing ? <><Loader2 className="w-4 h-4 animate-spin"/> Synchronizuję...</> : <>Zsynchronizuj listę</>}
        </button>
      </div>

      {/* Compose */}
      <div className="mt-6 bg-white border border-slate-200 rounded-3xl p-5 sm:p-6">
        <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Temat</label>
        <input
          data-testid="email-subject-input"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          className="w-full rounded-2xl border-2 border-slate-200 focus:border-[#FFD600] outline-none px-4 py-3"
        />

        <label className="block mt-4 text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">
          Treść <span className="font-normal normal-case text-slate-400">(tekst lub HTML — link wypisu dodamy automatycznie)</span>
        </label>
        <textarea
          data-testid="email-body-input"
          rows={10}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          className="w-full rounded-2xl border-2 border-slate-200 focus:border-[#FFD600] outline-none px-4 py-3 font-sans"
        />

        <div className="mt-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="text-sm text-slate-500">
            Odbiorcy z aktywną zgodą: <b className="text-slate-900" data-testid="email-recipient-count">{recipientCount}</b>
            {lastSendResult && (
              <span className="ml-2 text-xs text-slate-400">
                Ostatnia kampania: #{lastSendResult.campaign_id} {lastSendResult.sent ? "· wysłana" : "· draft"}
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <button
              data-testid="brevo-draft-button"
              onClick={() => sendCampaign(false)}
              disabled={sending || !status?.healthy}
              className="inline-flex items-center gap-2 text-sm font-bold text-slate-900 bg-white border border-slate-200 hover:border-slate-900 disabled:opacity-50 rounded-2xl px-4 py-2"
            >
              Utwórz draft
            </button>
            <button
              data-testid="brevo-send-button"
              onClick={() => sendCampaign(true)}
              disabled={sending || !status?.healthy}
              className="inline-flex items-center gap-2 text-sm font-bold text-slate-900 bg-[#FFD600] hover:bg-[#E6C200] disabled:opacity-50 rounded-2xl px-5 py-2 btn-yellow-glow"
            >
              {sending ? <><Loader2 className="w-4 h-4 animate-spin"/> Wysyłam...</> : <><Send className="w-4 h-4" /> Wyślij teraz</>}
            </button>
          </div>
        </div>
      </div>

      <div className="mt-4 text-xs text-slate-400 bg-slate-50 border border-slate-200 rounded-2xl p-4">
        <b>Jak to działa:</b> „Wyślij teraz" tworzy kampanię w Brevo i od razu ją dispatchuje. „Utwórz draft" tworzy szablon, który możesz podejrzeć i wysłać z panelu Brevo. Wszystko z automatycznym linkiem wypisu (RODO) na dole każdej wiadomości.
      </div>
    </div>
  );
}

function Skeleton() {
  return (
    <div className="flex items-center justify-center py-16">
      <Loader2 className="w-6 h-6 text-yellow-500 animate-spin" />
    </div>
  );
}


/* -------------------- Moderation tab -------------------- */
function formatRemaining(seconds) {
  const s = Math.max(0, Math.floor(seconds));
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function ModerationTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/violations");
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Nie udało się wczytać naruszeń");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const unmute = async (userId, email) => {
    if (!window.confirm(`Cofnąć wyciszenie dla ${email}? Zeruje też licznik naruszeń.`)) return;
    setActing(userId);
    try {
      await api.post(`/admin/violations/${userId}/unmute`);
      toast.success("Wyciszenie cofnięte");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Nie udało się cofnąć");
    } finally {
      setActing(null);
    }
  };

  if (loading && !data) return <Skeleton />;
  const items = data?.items || [];
  const activeCount = items.filter((i) => i.active_mute).length;

  return (
    <section data-testid="moderation-tab">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-extrabold tracking-tight text-slate-900">Naruszenia zasad</h2>
          <p className="text-sm text-slate-500 mt-1">
            Łącznie <b>{items.length}</b> użytkowników z naruszeniami, w tym <b>{activeCount}</b> obecnie wyciszonych.
          </p>
        </div>
        <button
          onClick={load}
          data-testid="moderation-refresh"
          className="text-xs font-bold text-slate-600 hover:text-slate-900 border border-slate-200 rounded-full px-3 py-1.5"
        >
          Odśwież
        </button>
      </div>

      {items.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-3xl p-10 text-center" data-testid="no-violations">
          <ShieldAlert className="w-10 h-10 mx-auto text-slate-300 mb-3" />
          <div className="text-slate-700 font-bold">Brak naruszeń</div>
          <div className="text-sm text-slate-500 mt-1">Żaden użytkownik nie łamał jeszcze zasad społeczności.</div>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-3xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3 font-bold">Użytkownik</th>
                  <th className="text-left px-4 py-3 font-bold">Naruszeń</th>
                  <th className="text-left px-4 py-3 font-bold">Ostatnie słowo</th>
                  <th className="text-left px-4 py-3 font-bold">Ostatnie naruszenie</th>
                  <th className="text-left px-4 py-3 font-bold">Status</th>
                  <th className="text-right px-4 py-3 font-bold">Akcja</th>
                </tr>
              </thead>
              <tbody>
                {items.map((v) => (
                  <tr key={v.user_id} className="border-t border-slate-100" data-testid={`violation-row-${v.user_id}`}>
                    <td className="px-4 py-3">
                      <div className="font-bold text-slate-900">{v.email}</div>
                      {v.name && <div className="text-xs text-slate-500">{v.name}</div>}
                    </td>
                    <td className="px-4 py-3 font-bold text-slate-900">{v.violations_count}</td>
                    <td className="px-4 py-3">
                      <code className="text-xs bg-red-50 text-red-700 px-2 py-1 rounded-md font-mono">
                        {v.last_matched_term || "—"}
                      </code>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-600">{fmtDate(v.last_violation_at)}</td>
                    <td className="px-4 py-3">
                      {v.active_mute ? (
                        <span className="inline-flex items-center gap-1 text-xs font-bold text-red-700 bg-red-50 border border-red-200 rounded-full px-2.5 py-1">
                          <VolumeX className="w-3 h-3" /> Wyciszony · zostało {formatRemaining(v.remaining_seconds)}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs font-bold text-slate-500 bg-slate-50 border border-slate-200 rounded-full px-2.5 py-1">
                          Wygasło
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => unmute(v.user_id, v.email)}
                        disabled={acting === v.user_id || !v.active_mute}
                        data-testid={`unmute-${v.user_id}`}
                        className="text-xs font-bold px-3 py-1.5 rounded-full bg-[#FFD600] hover:bg-[#E6C200] text-slate-900 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        {acting === v.user_id ? "..." : "Cofnij wyciszenie"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  );
}

/* -------------------- School accounts tab -------------------- */
function SchoolAccountsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [schoolName, setSchoolName] = useState("");
  const [durationDays, setDurationDays] = useState(365);
  const [busy, setBusy] = useState(false);
  const [lastCreated, setLastCreated] = useState(null); // {email, password} żeby admin mógł skopiować

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/accounts/school");
      setItems(data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Nie udało się wczytać kont");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const generatePassword = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";
    let p = "";
    for (let i = 0; i < 12; i++) p += chars[Math.floor(Math.random() * chars.length)];
    setPassword(p);
  };

  const create = async (e) => {
    e?.preventDefault?.();
    if (!email.trim() || !password || password.length < 6) {
      toast.error("Podaj email i hasło (min. 6 znaków)");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/accounts/school", {
        email: email.trim(),
        password,
        school_name: schoolName.trim() || null,
        duration_days: Number(durationDays) || 365,
      });
      toast.success(`Konto utworzone (ważne ${data.duration_days} dni)`);
      setLastCreated({ email: email.trim(), password, cycle_end: data.cycle_end });
      setEmail(""); setPassword(""); setSchoolName("");
      await load();
    } catch (ex) {
      toast.error(ex.response?.data?.detail || "Nie udało się utworzyć konta");
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (userId, userEmail) => {
    if (!window.confirm(`Odwołać konto ${userEmail}? Subskrypcja wygaśnie natychmiast, hasło zostanie wyłączone, wszystkie sesje — wylogowane.`)) return;
    try {
      await api.post(`/admin/accounts/school/${userId}/revoke`);
      toast.success("Konto odwołane");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Nie udało się odwołać");
    }
  };

  const copyCreds = () => {
    if (!lastCreated) return;
    const text = `Email: ${lastCreated.email}\nHasło: ${lastCreated.password}\nWażne do: ${new Date(lastCreated.cycle_end).toLocaleDateString("pl-PL")}\n\nZaloguj się: ${window.location.origin}`;
    navigator.clipboard.writeText(text);
    toast.success("Skopiowano do schowka");
  };

  return (
    <section data-testid="school-accounts-tab">
      <div className="mb-5">
        <h2 className="text-xl font-extrabold tracking-tight text-slate-900">Konta dla szkół</h2>
        <p className="text-sm text-slate-500 mt-1">
          Generuj konta z pełnym planem <b>Szkoły</b> i podanym czasem subskrypcji. Konto loguje się loginem i hasłem (bez Google).
        </p>
      </div>

      {/* Create form */}
      <form onSubmit={create} className="bg-white border border-slate-200 rounded-3xl p-5 sm:p-6 grid gap-3 sm:grid-cols-2">
        <div className="sm:col-span-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-400 mb-1">
          <UserPlus className="w-3.5 h-3.5" /> Nowe konto
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Email (login)</label>
          <input
            type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            data-testid="school-account-email"
            placeholder="kontakt@szkola.pl"
            className="w-full rounded-2xl border-2 border-slate-200 focus:border-[#FFD600] outline-none px-4 py-3 text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Nazwa szkoły (opcjonalnie)</label>
          <input
            value={schoolName} onChange={(e) => setSchoolName(e.target.value)}
            data-testid="school-account-name"
            placeholder="II LO im. Batorego"
            className="w-full rounded-2xl border-2 border-slate-200 focus:border-[#FFD600] outline-none px-4 py-3 text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Hasło (min. 6)</label>
          <div className="flex gap-2">
            <input
              type="text" value={password} onChange={(e) => setPassword(e.target.value)}
              data-testid="school-account-password"
              placeholder="••••••••"
              className="flex-1 rounded-2xl border-2 border-slate-200 focus:border-[#FFD600] outline-none px-4 py-3 text-sm font-mono"
            />
            <button
              type="button" onClick={generatePassword}
              data-testid="school-account-generate-password"
              className="inline-flex items-center gap-1 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold px-3 py-2 rounded-2xl"
            >
              <Key className="w-3.5 h-3.5"/> Losuj
            </button>
          </div>
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Czas trwania (dni)</label>
          <div className="flex gap-2">
            <input
              type="number" min="1" max="3650"
              value={durationDays} onChange={(e) => setDurationDays(e.target.value)}
              data-testid="school-account-duration"
              className="flex-1 rounded-2xl border-2 border-slate-200 focus:border-[#FFD600] outline-none px-4 py-3 text-sm"
            />
            {[30, 90, 180, 365].map((d) => (
              <button
                type="button" key={d} onClick={() => setDurationDays(d)}
                className={`text-xs font-bold px-2.5 rounded-2xl ${Number(durationDays) === d ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"}`}
              >
                {d}d
              </button>
            ))}
          </div>
        </div>
        <div className="sm:col-span-2">
          <button
            type="submit" disabled={busy}
            data-testid="school-account-create"
            className="w-full inline-flex items-center justify-center gap-2 bg-[#FFD600] hover:bg-[#E6C200] text-slate-900 font-bold px-6 py-3.5 rounded-2xl disabled:opacity-50"
          >
            {busy ? <><Loader2 className="w-4 h-4 animate-spin"/> Tworzę...</> : <><UserPlus className="w-4 h-4" strokeWidth={2.5}/> Utwórz konto szkolne</>}
          </button>
        </div>

        {lastCreated && (
          <div className="sm:col-span-2 bg-[#FEF9C3] border border-yellow-300 rounded-2xl p-4 flex items-start gap-3" data-testid="school-account-last-created">
            <Key className="w-5 h-5 text-slate-900 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-xs font-bold uppercase tracking-wider text-slate-700">Konto utworzone — zapisz dane</div>
              <div className="mt-1 text-sm font-mono text-slate-900">
                <div>Email: <b>{lastCreated.email}</b></div>
                <div>Hasło: <b>{lastCreated.password}</b></div>
                <div className="text-xs text-slate-500 mt-1">
                  Ważne do: {new Date(lastCreated.cycle_end).toLocaleDateString("pl-PL")}
                </div>
              </div>
            </div>
            <button type="button" onClick={copyCreds} className="inline-flex items-center gap-1 text-xs font-bold bg-white hover:bg-slate-50 border border-slate-200 rounded-full px-3 py-1.5">
              <Copy className="w-3 h-3"/> Kopiuj
            </button>
          </div>
        )}
      </form>

      {/* List */}
      <div className="mt-6">
        <h3 className="text-sm font-bold tracking-tight text-slate-900 mb-3">Istniejące konta ({items.length})</h3>
        {loading ? <Skeleton /> : items.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-3xl p-10 text-center">
            <School className="w-10 h-10 mx-auto text-slate-300 mb-3"/>
            <div className="text-slate-700 font-bold">Brak kont szkolnych</div>
            <div className="text-sm text-slate-500 mt-1">Utwórz pierwsze konto formularzem powyżej.</div>
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-3xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="text-left px-4 py-3 font-bold">Konto</th>
                    <th className="text-left px-4 py-3 font-bold">Utworzono</th>
                    <th className="text-left px-4 py-3 font-bold">Subskrypcja</th>
                    <th className="text-right px-4 py-3 font-bold">Akcja</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it) => {
                    const sub = it.subscription || {};
                    const active = sub.status === "active";
                    return (
                      <tr key={it.user_id} className="border-t border-slate-100">
                        <td className="px-4 py-3">
                          <div className="font-bold text-slate-900">{it.email}</div>
                          {it.name && it.name !== it.email && <div className="text-xs text-slate-500">{it.name}</div>}
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-600">{fmtDate(it.created_at)}</td>
                        <td className="px-4 py-3">
                          {active ? (
                            <span className="inline-flex items-center gap-1 text-xs font-bold text-green-700 bg-green-50 border border-green-200 rounded-full px-2.5 py-1">
                              Aktywna · do {sub.cycle_end ? new Date(sub.cycle_end).toLocaleDateString("pl-PL") : "—"}
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-xs font-bold text-slate-500 bg-slate-50 border border-slate-200 rounded-full px-2.5 py-1">
                              {sub.status === "revoked" ? "Odwołana" : "Wygasła"}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {active && (
                            <button
                              onClick={() => revoke(it.user_id, it.email)}
                              data-testid={`revoke-${it.user_id}`}
                              className="inline-flex items-center gap-1 text-xs font-bold px-3 py-1.5 rounded-full bg-red-50 hover:bg-red-100 text-red-700 border border-red-200"
                            >
                              <Trash2 className="w-3 h-3"/> Odwołaj
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

